<template>
  <MainLayout>
    <div class="container mw-960" slot="action-bar">
      <div class="row">
        <div class="col-12">
          <h5 class="action-header typography--headline typography--color-secondary pt-1 pl-10-mobile pr-10-mobile">{{ formattedClassName }}</h5>
        </div>
      </div>
    </div>

    <div class="container mw-960 mt-15" slot="layout-body">
      <div class="row pt-3" v-if="loading">
        <div class="col-12 text-center">
          <LoadingBounce></LoadingBounce>
          <div class="typography--body-2 typography--color-disabled pb-5">{{ $t('common.loading') }}</div>
        </div>
      </div>

      <div class="row" v-else>

        <div class="row sort-subject-menu">
          <b-dropdown v-bind:text="getTranslatedName(subjectMap[selectedSubjectId])" class="select-subject-dropdown mnv-dropdown">
            <b-dropdown-item
              v-for="(value, index) in subjectIds"
              :key="index"
              v-on:click="selectedSubjectId = parseInt(value, 10); sortAssignments();"
            >
              {{ getTranslatedName(subjectMap[value]) }}
            </b-dropdown-item>
          </b-dropdown>

          <div class="sort-type clickable mr-12 typography--color-secondary" v-on:click="sortType = 'recent'; sortAssignments();">
            <img src="/static/images/radio-btn-01.svg" v-if="sortType === 'recent'">
            <img src="/static/images/radio-btn-02.svg" v-else> {{ $t('reports.sort_new_assignment') }}</div>
          <div class="sort-type clickable typography--color-secondary" v-on:click="sortType = 'score'; sortAssignments();">
            <img src="/static/images/radio-btn-01.svg" v-if="sortType === 'score'">
            <img src="/static/images/radio-btn-02.svg" v-else> {{ $t('reports.sort_achievement') }}</div>
        </div>

        <div class="col-12">
          <!-- assignment results report -->
          <div class="card-row">

            <TableCard class="report-result">
              <div slot="table-action" class="mb-8">
                <div class="typography--content-body pb-3">
                  {{ getShortenedString(currentStudent.name, 30) }}
                </div>

                <div class="float-right typography--caption typography--color-secondary pb-2">
                  <span v-if="assignmentsBySubjects[selectedSubjectId]">
                    {{ $t('reports.assignment_count_cnt', { cnt: assignmentsBySubjects[selectedSubjectId].length }) }}
                  </span>
                  <span v-else>{{ $t('reports.assignment_count_cnt', { cnt: 0 }) }}</span>
                </div>
              </div>
              <table class="table" slot="table">
                <thead>
                  <tr>
                    <th class="align-top typography--color-secondary text-left pl-10">
                      <div>{{ $t('reports.table.card_name') }}</div>
                    </th>
                    <th class="align-top typography--color-secondary">
                      <div>{{ $t('reports.table.score') }}</div>
                    </th>
                    <th class="align-top typography--color-secondary">
                      {{ $t('reports.table.class_rank') }}
                    </th>
                    <th class="align-top typography--color-secondary">
                      {{ $t('reports.table.correct_count') }}
                    </th>
                    <th class="align-top typography--color-secondary">
                      {{ $t('reports.table.duration') }}
                    </th>
                    <th class="align-top typography--color-secondary">
                      {{ $t('reports.assignment_period') }}
                    </th>
                    <th class="align-top typography--color-secondary">
                      {{ $t('reports.see_result') }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <template v-if="(assignmentsBySubjects[selectedSubjectId] || []).length > 0">
                    <tr v-for="(asmt, index) in assignmentsBySubjects[selectedSubjectId] || []" v-bind:key="index">
                      <td class="align-middle typography--body-3 card-info-cell pl-10">
                        <div class="align-middle clickable"
                            v-on:click="openStudentAssignmentPreview(asmt)">
                          {{ getShortenedString(asmt.assigned_card.title, 30) }}
                        </div>
                      </td>

                      <!-- Assignment score ratio -->
                      <td class="align-top typography--body-3 text-center"
                          v-bind:class="{ 'typography--color-alert': getScoreRatio(asmt.currentStudentSubmission) <= 20 }"
                          v-if="asmt.currentStudentSubmission.is_submitted">
                        {{ getScoreRatio(asmt.currentStudentSubmission) || 0 }}
                      </td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center" v-else>-</td>

                      <!-- Assignment score -->
                      <td class="align-top typography--body-3 text-center"
                          v-if="asmt.currentStudentSubmission.is_submitted">
                        <span class="typography--font-weight-bold">
                          {{ getClassScoreRank(asmt.currentStudentSubmission.correct_quiz_count, asmt.classScores) }}{{ $t('reports.rank_suffix') }}
                        </span> / {{ getNumStrTwoDigit(asmt.submissions.length) }}
                      </td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center" v-else>-</td>

                      <!-- Assignment rank in class -->
                      <td class="align-top typography--body-3 text-center" v-if="asmt.currentStudentSubmission.is_submitted">
                        <span class="typography--font-weight-bold correct-quiz-count">{{ getNumStrTwoDigit(asmt.currentStudentSubmission.correct_quiz_count) }}</span>
                          / {{ asmt.currentStudentSubmission.quiz_count }}
                      </td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center" v-else>-</td>

                      <!-- Average duration -->
                      <td class="align-top typography--body-3 text-center" v-if="asmt.currentStudentSubmission.is_submitted">
                        {{ asmt.currentStudentSubmission.duration | duration }}
                      </td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center" v-else>-</td>

                      <td class="align-top typography--body-3 text-center">{{ formattedDate(asmt.start_date) }} - {{ formattedDate(asmt.due_date) }}</td>

                      <td class="align-top typography--body-3 text-center">
                        <div class="btn-outline-rectangle clickable" v-on:click="openStudentAssignmentPreview(asmt)">{{ $t('reports.assignment_result') }}</div>
                      </td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr >
                      <td class="align-top typography--body-3 typography--color-disabled text-center">-</td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center">-</td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center">-</td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center">-</td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center">-</td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center">-</td>
                      <td class="align-top typography--body-3 typography--color-disabled text-center">-</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </TableCard>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isEmpty, keyBy, orderBy, mean, cloneDeep } from 'lodash';
  import labels from '@/mixins/labels';

  export default {
    name: 'student-report',

    mixins: [
      labels,
    ],

    data() {
      return {
        loading: false,
        cls: null,
        currentStudent: {},
        selectedSubjectId: 0,
        students: null,
        assignments: [],
        assignmentsBySubjects: {},
        sortType: 'recent',
      };
    },

    computed: {
      ...mapGetters([
        'managingClasses',
        'getRouteQuery',
        'coreConcepts',
        'subjectMap',
      ]),

      subjectIds() {
        if (this.cls.grade >= 3 && this.cls.grade <= 6) {
          return [0, 2, 3, 5, 41, 42, 50, 51, 47, 48];
        } else if (this.cls.grade >= 7 && this.cls.grade <= 9) {
          return [0, 2, 5, 43, 44, 41, 42, 50, 51, 47, 48];
        }

        return [0, 41, 42, 50, 51, 47, 48, 49];
      },

      formattedClassName() {
        if (this.cls) {
          const year = this.cls.year;
          const schoolName = this.cls.school_name;
          const grade = this.cls.grade;
          const className = this.cls.name;

          return `${year} ${this.getShortenedString(schoolName, 10)} ${grade} - ${this.getShortenedString(className, 25)}`;
        }

        return '';
      },
    },

    methods: {
      ...mapActions([
        'setIsStudentAssignmentPreview',
        'setSelectedPreviewPageIndex',
        'fetchTeacherAssignmentStats',
        'fetchAssignedCardPages',
        'setSelectedPreviewCard',
        'fetchClassAssignments',
        'fetchClassStudents',
        'syncClassStudents',
      ]),

      formattedDate(dateStr) {
        return `${dateStr.substr(2, 2)}.${dateStr.substr(5, 2)}.${dateStr.substr(8, 2)}`;
      },

      getNumStrTwoDigit(number) {
        if (number <= 9) {
          return `0${number}`;
        }
        return number;
      },

      getLearningElements(coreConcept) {
        if (!coreConcept) {
          return null;
        }

        return coreConcept.description;
      },

      getScoreRatio(submission) {
        if (submission.quiz_count === 0) {
          return null;
        }

        return parseInt((submission.correct_quiz_count / submission.quiz_count) * 100, 10);
      },

      getClassScoreRank(correctQuizCount, classScores) {
        const classScoreRank = orderBy(classScores, null, 'desc').indexOf(correctQuizCount);

        if (classScoreRank === -1) {
          return null;
        }

        return classScoreRank + 1;
      },

      getClassScoreRankRatio(correctQuizCount, classScores, submissions) {
        const classScoreRank = this.getClassScoreRank(correctQuizCount, classScores);

        return parseInt((classScoreRank / submissions.length) * 100, 10);
      },

      getSubmittedSubmissions(submissions) {
        if (!submissions) {
          return [];
        }

        return submissions.filter(submission => (
          !!submission.is_submitted
        ));
      },

      getAverageScore(submissions) {
        if (!submissions) {
          return null;
        }

        const filtered = submissions.filter(submission => (
          submission.quiz_count > 0
        ));

        if (filtered.length === 0) {
          return null;
        }

        const scores = this.getSubmittedSubmissions(filtered).map(submission => (
          (submission.correct_quiz_count / submission.quiz_count) * 100
        ));

        if (scores.length === 0) {
          return null;
        }

        return parseInt(mean(scores), 10);
      },

      getAverageDuration(submissions) {
        if (!submissions) {
          return null;
        }

        const filtered = this.getSubmittedSubmissions(submissions).filter(submission => (
          submission.duration !== null
        ));

        if (filtered.length === 0) {
          return null;
        }

        const durations = filtered.map(submission => (
          submission.duration
        ));

        return parseInt(mean(durations), 10);
      },

      getCompleteRate(submissions) {
        if (!submissions || submissions.length === 0) {
          return 0;
        }

        const submittedCount = this.getSubmittedSubmissions(submissions).length;
        const rate = (submittedCount / submissions.length) * 100;

        return parseInt(rate, 10);
      },

      openStudentAssignmentPreview(assignment) {
        const cardClone = cloneDeep(assignment.assigned_card);

        cardClone.title += ` (${this.currentStudent.name})`;

        this.fetchAssignedCardPages(assignment.id)
          .then((pages) => {
            const quizPages = pages.filter(page => (!!page.is_quiz));
            cardClone.pages = cloneDeep(quizPages);

            cardClone.pages.forEach((page) => {
              /* eslint-disable no-param-reassign */
              page.submitted_answers = page.submitted_answers.filter(answer => (
                answer.student_id === this.currentStudent.student_id
              ));
            });
          })
          .then(() => (
            this.setSelectedPreviewCard(cardClone)
          ))
          .then(() => (
            this.setSelectedPreviewPageIndex(0)
          ))
          .then(() => (
            this.setIsStudentAssignmentPreview(true)
          ))
          .then(() => {
            this.$root.$emit('show::modal', 'card-preview-modal');
          })
          .catch((err) => {
            this.$root.$emit('show::modal', 'basic-error-alert');
            return Promise.reject(err);
          });
      },

      sortAssignments() {
        if (this.sortType === 'recent') {
          this.assignmentsBySubjects[this.selectedSubjectId] = orderBy(
            this.assignmentsBySubjects[this.selectedSubjectId], assignment => (assignment.due_date), 'desc',
          );
          this.assignmentsBySubjects = cloneDeep(this.assignmentsBySubjects);
        } else {
          this.assignmentsBySubjects[this.selectedSubjectId] =
            orderBy(this.assignmentsBySubjects[this.selectedSubjectId], assignment => (this.getScoreRatio(assignment.currentStudentSubmission) || 0), 'desc');
          this.assignmentsBySubjects = cloneDeep(this.assignmentsBySubjects);
        }
      },

      getShortenedString(string, maxLength) {
        if (string.length <= maxLength) {
          return string;
        }
        return `${string.substring(0, maxLength)}...`;
      },
    },

    created() {
      this.loading = true;
      const classId = this.$route.params.class_id;
      const studentId = this.$route.params.student_id;

      this.cls = this.managingClasses[classId];

      if (!isEmpty(this.cls)) {
        this.fetchClassStudents(this.cls.id)
          .then((studentList) => {
            if (!studentList || studentList.length === 0) {
              return this.syncClassStudents(this.cls.id);
            }

            return studentList;
          })
          .then((studentList) => {
            this.students = keyBy(studentList, 'student_id');
            this.currentStudent = this.students[studentId] || {};

            if (isEmpty(this.currentStudent)) {
              return Promise.reject('student not found in this class');
            }

            return this.currentStudent;
          })
          .then(() => (
            this.fetchTeacherAssignmentStats({ class_id: classId })
              .then((assignmentStats) => {
                this.assignmentStats = assignmentStats;

                return assignmentStats;
              })
          ))
          .then(assignmentStats => (
            this.fetchClassAssignments({ class_id: classId, per_page: assignmentStats.total_count })
          ))
          .then(assignments => (
            assignments.filter(asmt => (
              asmt.submissions.map(submission => (
                submission.student_id
              ))
              .indexOf(this.currentStudent.student_id) !== -1
            ))
          ))
          .then((assignments) => {
            this.assignments = assignments;
          })
          .then(() => {
            this.assignmentsBySubjects[0] = [];

            this.assignments.forEach((assignment) => {
              if (isEmpty(this.assignmentsBySubjects[assignment.assigned_card.subject_id])) {
                this.assignmentsBySubjects[assignment.assigned_card.subject_id] = [];
              }

              this.assignmentsBySubjects[0].push(assignment);
              this.assignmentsBySubjects[assignment.assigned_card.subject_id].push(assignment);

              if (isEmpty(assignment.classScores)) {
                assignment.classScores = [];
              }

              assignment.submissions.forEach((submission) => {
                if (submission.student_id === this.currentStudent.student_id) {
                  assignment.currentStudentSubmission = submission;
                }

                if (submission.is_submitted) {
                  assignment.classScores.push(submission.correct_quiz_count);
                }

                if (this.students[submission.student_id]) {
                  if (isEmpty(this.students[submission.student_id].submissions)) {
                    this.students[submission.student_id].submissions = [];
                  }

                  this.students[submission.student_id].submissions.push(submission);
                }
              });
            });
          })
          .then(() => {
            if (this.getRouteQuery.subject_id) {
              this.selectedSubjectId = this.getRouteQuery.subject_id;
            }
            this.sortAssignments();
            this.loading = false;
          })
          .catch((err) => {
            this.$root.$emit('show::modal', 'basic-error-alert');
            return Promise.reject(err);
          });
      } else {
        this.loading = false;
        this.$root.$emit('show::modal', 'basic-error-alert');
      }
    },
  };
</script>

<style lang="scss">
  .sort-subject-menu {
    padding-left: 20px;
    height: 50px;

    .sort-type {
      img {
        margin-top: -2px;
      }
    }

    .select-subject-dropdown {
      position: absolute;
      display: inline-block;
      right: 10px;
      width: 150px !important;
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/classes/views/StudentReport.vue